import {
  Component,
  OnInit,
  ElementRef,
  ViewChild,
  OnDestroy,
  ChangeDetectorRef
} from '@angular/core';
import {
  FormGroup,
  FormControl,
  Validators,
  FormArray,
  AbstractControl,
  FormBuilder,
  ValidationErrors
} from '@angular/forms';
import { Subscription, BehaviorSubject } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { DateUtilities } from '@approot/utilities';
import { Fixing, FixingSource, KeyValue } from '@approot/models';
import {
  LegService,
  ProductsConfigService,
  PricingExtraService,
  MarketsService,
  AuthService
} from '@approot/services';
import dayjs from 'dayjs';

@Component({
  selector: 'app-fixings',
  templateUrl: './fixings.component.html',
  styleUrls: ['./fixings.component.scss']
})
export class FixingsComponent implements OnInit, OnDestroy {
  fixingForm: FormGroup;
  selectedDateIndex: number = null; // this holds the index of the date that is currently selected
  selectedFixingIndex: number = null; // index of the fixing selected
  currencyPairs: string[] = [];
  fixingSourcesLongNames: string[] = [];
  fixingSources: FixingSource[] = [];
  fixingsToDelete: {
    timestamp: number;
    userId: string;
  }[] = [];
  areTenorsLoading = false;
  completeFixingsForCurrency: Fixing[];
  fixingsArray = new FormArray([], this.uniqueDateValidator);
  currentSubscription: Subscription;
  @ViewChild('divToScroll', { static: false }) divToScroll: ElementRef;

  private _fixings = new BehaviorSubject<Fixing[]>([]);
  readonly fixings$ = this._fixings.asObservable();

  constructor(
    private marketsService: MarketsService,
    private pricingExtraService: PricingExtraService,
    private legService: LegService,
    private productsConfigService: ProductsConfigService,
    private fb: FormBuilder,
    private changeDetectorRef: ChangeDetectorRef,
    private authService: AuthService
  ) {}

  ngOnInit() {
    this.fixingForm = this.fb.group({
      currencyPairs: this.fb.control('', [Validators.required]),
      fixingSources: this.fb.control('', [Validators.required]),
      fixings: this.fixingsArray
    });

    this.fixingForm.get('currencyPairs').valueChanges.subscribe(cross => {
      if (cross) {
        // when we select a new currency pair, we need to reset the available fixing sources
        this.fixingSources = [];
        this.fixingSourcesLongNames = [];

        if (this.fixingForm.get('fixingSources').value) {
          this.fixingForm.get('fixingSources').reset();
        }

        if (this.fixingsArray.length) {
          this.fixingsArray.clear();
        }

        this.authService
          .fxPost(
            {
              task: 'getCurrencyPairFixingSourceDefinitions',
              currencyPair: cross
            },
            'get-user-info',
            ''
          )
          .pipe(
            map((fixingSources: FixingSource[]) => {
              this.fixingSources = fixingSources;
              this.fixingSourcesLongNames = fixingSources.map(_ => _.longName);
            }),
            take(1)
          )
          .subscribe();
      }
    });

    // when the fixing source has been selected, populate the fixings
    this.fixingForm.get('fixingSources').valueChanges.subscribe(val => {
      if (val) {
        this.areTenorsLoading = true;
        this.fixingsArray.clear();
        this.legService
          .marketDataService({
            task: 'getFixingsForCurrencyPairAndCode',
            currencyPair: this.fixingForm.get('currencyPairs').value,
            code: this.fixingSources.find(_ => _.longName === val).code
          })
          .pipe(
            map((res: { fixings: Fixing[] }) => {
              this.areTenorsLoading = false;
              this._fixings.next(res.fixings);
              res.fixings.forEach((fixing: Fixing) => {
                const tempDateGroup = new FormGroup({
                  date: new FormControl(
                    DateUtilities.dateToPrettyDate(fixing.date)
                  ),
                  spot: new FormControl(+(+fixing.spot).toFixed(6))
                });

                tempDateGroup
                  .get('date')
                  .setValidators([
                    this.dateControlValidator.bind(tempDateGroup.get('date'))
                  ]);

                tempDateGroup
                  .get('spot')
                  .setValidators([
                    this.numberControlValidator.bind(tempDateGroup.get('spot')),
                    Validators.required
                  ]);

                this.fixingsArray.push(tempDateGroup);
              });
            }),
            take(1)
          )
          .subscribe();
      }
    });

    let cp =
      this.productsConfigService.cutOffTimesAndFixingSources.currencyPairInfo.map(
        x => {
          return x.currencyPair;
        }
      );
    this.currencyPairs = cp;
  }

  public ngOnDestroy(): void {
    clearTimeout(this.marketsService.timer);
    this.marketsService.processingInfo = '';
    this.marketsService.currentAction?.unsubscribe();
    this.currentSubscription?.unsubscribe();
  }

  createDateRange(monthsAgo: number) {
    let d = new Date();
    let today = DateUtilities.getDateInISOFormat(d);
    d.setMonth(d.getMonth() - monthsAgo);
    let twoMonthsAgo = DateUtilities.getDateInISOFormat(d);
    return twoMonthsAgo + '-' + today;
  }

  get showFixings() {
    return this.marketsService.showFixings;
  }

  set showFixings(val) {
    this.marketsService.showFixings = val;
  }

  modalChanged(event) {
    // the timeout may still be running if the user decides to close the screen early, so clear it
    clearTimeout(this.marketsService.timer);
    this.marketsService.processingInfo = '';
    this.marketsService.currentAction?.unsubscribe();
    this.currentSubscription?.unsubscribe();
    this.marketsService.showFixings = event;
  }

  returnDates() {
    return (this.fixingForm.get('fixings') as FormArray).controls;
  }

  removeFixing(fixingIndex: number) {
    (this.fixingForm.get('fixings') as FormArray).removeAt(fixingIndex);
  }

  removeDate() {
    (
      (this.fixingForm.get('fixings') as FormArray).controls[
        this.selectedFixingIndex
      ].get('fixings') as FormArray
    ).removeAt(this.selectedDateIndex);

    (this.fixingForm.get('fixings') as FormArray).controls[
      this.selectedFixingIndex
    ]
      .get('fixings')
      .markAsDirty();

    this.selectedDateIndex = null;
  }

  addNewDate() {
    const tempGroup = new FormGroup({
      date: new FormControl(''),
      spot: new FormControl(''),
      canBeRemoved: new FormControl(true)
    });

    tempGroup
      .get('date')
      .setValidators([this.dateControlValidator.bind(tempGroup.get('date'))]);

    tempGroup
      .get('spot')
      .setValidators([
        this.numberControlValidator.bind(tempGroup.get('spot')),
        Validators.required
      ]);

    (this.fixingForm.get('fixings') as FormArray).push(tempGroup);
    setTimeout(() => {
      this.updateScroll();
    }, 0);
    this.changeDetectorRef.detectChanges();
  }

  updateScroll() {
    let e = this.divToScroll.nativeElement;
    e.scrollTop = e.scrollHeight;
  }

  get processingInfo(): string {
    // hold the status of the information sent to the database
    return this.marketsService.processingInfo;
  }

  onSaveFixingChanges() {
    // we only want to save fixings that have been changed. So we filter the controls
    // and only keep the dirty ones for processing
    const filteredDirty = (
      this.fixingForm.get('fixings') as FormArray
    ).controls.filter((x: AbstractControl) => {
      return x.dirty === true;
    });

    // fixing source and currencypair will be the same for all dirty records
    const _fixingSource = this.fixingForm.get('fixingSources').value;
    const _currencyPair = this.fixingForm.get('currencyPairs').value;

    const _fixingSourceShort =
      this.productsConfigService.cutOffTimesAndFixingSources[
        'fixingSources'
      ].filter(x => x.longName === _fixingSource)[0];

    // attach the necessary data, the counter 'i' is used to add a millisecond to each records'
    // timestamp so that the dynamodb record will be unique by userId and timestamp
    let i = 0;
    filteredDirty.map(y => {
      i++;
      (y.value['objectType'] = 'fixing'),
        (y.value['fixingSource'] = _fixingSourceShort.code),
        (y.value['date'] = DateUtilities.prettyDateToDate(y.value['date'])),
        (y.value['currencyPair'] = _currencyPair),
        (y.value['timestamp'] = new Date().setMilliseconds(i));
    });

    if (filteredDirty.length) {
      this.marketsService.saveFixing(
        filteredDirty.map(x => x.value),
        this.fixingForm
      );
    }
  }

  addNewFixing() {
    let newFixing = new FormGroup({
      timestamp: new FormControl(new Date().getTime()),
      currencyPair: new FormControl(''),
      fixingSource: new FormControl('', Validators.required),
      fixings: new FormArray([]),
      objectType: new FormControl('Fixings'),
      userId: new FormControl(''),
      updateInfo: new FormControl([]),
      saved: new FormControl(false)
    });
    // saved indicates if the fixing has been saved previously

    newFixing
      .get('currencyPair')
      .setValidators([
        this.validatorCurrencyPair.bind(newFixing.get('currentPair')),
        Validators.required,
        Validators.minLength(6)
      ]);

    (this.fixingForm.get('fixings') as FormArray).push(newFixing);
  }

  currencyPairChange(
    currencyPairControl: AbstractControl,
    fixingSourceControl: AbstractControl
  ) {
    // once the currency pair is in a valid state, all dates that are not of the correct format wipe them
    fixingSourceControl.setValue('');

    if (currencyPairControl.valid) {
      const fixingsDates = (
        (this.fixingForm.get('fixings') as FormArray).controls[
          this.selectedFixingIndex
        ].get('fixings') as FormArray
      ).controls;

      fixingsDates.forEach(date => {
        if (!DateUtilities.isPrettyDate(date.get('date').value)) {
          date.get('date').setValue('');
        }
      });
    }
  }

  onDateChange() {
    // gets the next fixing date
    // const dateElem = ((this.fixingForm.get('fixings') as FormArray).controls[
    //   this.selectedFixingIndex
    // ].get('fixings') as FormArray).controls[this.selectedDateIndex].get('date');
    const dateElem = (this.fixingForm.get('fixings') as FormArray).controls[
      this.selectedDateIndex
    ].get('date');

    const cross = this.fixingForm.get('currencyPairs').value;

    if (dateElem.valid && cross.length === 6) {
      const payload = DateUtilities.dateOrLagToPayload(
        dateElem.value.toLowerCase(),
        cross,
        this.pricingExtraService.getMarketDataTimeStamp
      );

      const filledDate = this.getDateFromCurrentYear(
        dateElem.value.toLowerCase()
      );
      if (filledDate)
        dateElem.patchValue(DateUtilities.dateToPrettyDate(filledDate), {
          emitEvent: false
        });
    }
  }

  uniqueDateValidator(formArray: AbstractControl): ValidationErrors | null {
    const dateArray = formArray.value.map((item: any) => item.date);
    const hasDuplicate = dateArray.some(
      (date: string, index: number) => dateArray.indexOf(date) !== index
    );
    return hasDuplicate ? { duplicateDates: true } : null;
  }

  dateControlValidator(control: FormControl) {
    if (
      DateUtilities.isCorrectDateFormat(control.value) ||
      DateUtilities.isValidLag(control.value)
    ) {
      return null;
    }
    return { invalidEntry: { value: control.value } };
  }

  numberControlValidator(control: FormControl) {
    if (
      !isNaN(control.value) &&
      new RegExp('^\\d+\\.?\\d{0,6}$').test(control.value)
    ) {
      return null;
    }
    return { invalidEntry: { value: control.value } };
  }

  validatorCurrencyPair(control: AbstractControl): KeyValue | null {
    if (new RegExp('^[A-Z]+$').test(control.value)) {
      return null;
    } else if (new RegExp('^[A-Za-z]+$').test(control.value)) {
      control.setValue(control.value.toUpperCase());
      return null;
    }
    return { invalidEntry: { value: control.value } };
  }

  private getDateFromCurrentYear(date: string) {
    // date as dd MMM
    const dateWithYear = dayjs(`${date} ${dayjs().year()}`);
    if (!dateWithYear.isValid()) {
      return 'Invalid date';
    }
    return dateWithYear.format('YYYY-MM-DD');
  }
}
